import { useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Download, Upload } from 'lucide-react';

function Summary({ s }) {
  const parts = [
    ['insert', s.insert], ['update', s.update], ['replace', s.replace], ['skip', s.skip], ['error', s.error],
  ].filter(([, n]) => n > 0);
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '8px 0' }}>
      {parts.map(([k, n]) => (
        <span key={k} className={`badge ${k === 'error' ? 'badge-inactive' : 'badge-active'}`}>{n} {k}</span>
      ))}
      {!parts.length && <span className="text-muted text-sm">nothing to do</span>}
    </div>
  );
}

function ResultRows({ rows }) {
  const shown = rows.slice(0, 200);
  return (
    <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6, marginTop: 6 }}>
      <table style={{ fontSize: 12 }}>
        <tbody>
          {shown.map((r, i) => (
            <tr key={i} style={r.action === 'error' ? { background: '#fff2f2' } : undefined}>
              <td style={{ padding: '3px 8px', color: 'var(--gray-500)' }}>ln {r.line}</td>
              <td style={{ padding: '3px 8px', fontWeight: 600, color: r.action === 'error' ? 'var(--brand-red)' : 'var(--gray-700)' }}>{r.action}</td>
              <td style={{ padding: '3px 8px' }}>{r.key || ''}</td>
              <td style={{ padding: '3px 8px', color: r.action === 'error' ? 'var(--brand-red)' : 'var(--gray-500)' }}>{r.error || r.note || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > shown.length && <p className="form-hint" style={{ padding: 8 }}>…and {rows.length - shown.length} more rows.</p>}
    </div>
  );
}

function ImportCard({ type, title, description, columns, hasBlankPolicy }) {
  const [file, setFile] = useState(null);
  const [policy, setPolicy] = useState('leave');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (mode) => {
    if (!file) return toast.error('Choose a CSV file first');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('blank_policy', policy);
      const r = await api.post(`/api/imports/${type}/${mode}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (mode === 'preview') { setPreview(r.data); }
      else {
        toast.success('Import complete');
        setPreview(r.data);
        setFile(null);
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Import failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="card">
      <div className="card-header"><h3>{title}</h3></div>
      <p className="form-hint" style={{ marginTop: 0 }}>{description}</p>
      <p className="text-sm" style={{ color: 'var(--gray-600)' }}><strong>Columns:</strong> <code style={{ fontSize: 12 }}>{columns}</code></p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
        <a className="btn btn-ghost btn-sm" href={`/api/imports/${type}/template`}>
          <Download size={13} /> Download template
        </a>
        <input type="file" accept=".csv,text/csv" onChange={e => { setFile(e.target.files[0]); setPreview(null); }} />
      </div>

      {hasBlankPolicy && (
        <div style={{ marginTop: 10 }}>
          <label className="form-label">When updating an existing row, blank cells should:</label>
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="radio" checked={policy === 'leave'} onChange={() => setPolicy('leave')} /> Leave unchanged
            </label>
            <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="radio" checked={policy === 'overwrite'} onChange={() => setPolicy('overwrite')} /> Overwrite with blank
            </label>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn btn-secondary" onClick={() => send('preview')} disabled={busy || !file}>
          {busy ? 'Working…' : 'Preview'}
        </button>
        <button className="btn btn-primary" onClick={() => send('commit')} disabled={busy || !preview || preview.committed}>
          <Upload size={14} /> Commit import
        </button>
      </div>

      {preview && (
        <div style={{ marginTop: 12 }}>
          <strong style={{ fontSize: 13 }}>{preview.committed ? 'Imported:' : 'Preview (nothing written yet):'}</strong>
          <Summary s={preview.summary} />
          <ResultRows rows={preview.rows} />
        </div>
      )}
    </div>
  );
}

export default function ImportsPage() {
  return (
    <>
      <div className="topbar"><h2>Imports</h2></div>
      <div className="page">
        <p className="form-hint" style={{ maxWidth: 720 }}>
          Upload a CSV to add or update records in bulk. Download the template for each,
          fill it in, then <strong>Preview</strong> to see exactly what will change before you <strong>Commit</strong>.
          Existing records are updated; new ones are inserted. Customers are matched by slug.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 760 }}>
          <ImportCard
            type="items" title="Items" hasBlankPolicy
            description="Add or update catalog items, matched by SKU."
            columns="sku, name, description, category, price_unit(per_m|per_case), case_qty, case_qty_label, is_generic(yes/no), active(yes/no)"
          />
          <ImportCard
            type="pricing" title="Customer Pricing"
            description="Set price tiers per item. Rows with the same sku+customer form the tiers; a blank customer_slug sets the base price. Importing replaces existing tiers for each sku+customer in the file."
            columns="sku, customer_slug(blank=base), qty_from, qty_to(blank=unlimited), price"
          />
          <ImportCard
            type="addresses" title="Customer Addresses" hasBlankPolicy
            description="Add or update ship-to addresses, matched by customer + nickname."
            columns="customer_slug, nickname, attention, address1, address2, city, state, zip, country, phone, inside_delivery(yes/no), lift_gate(yes/no)"
          />
          <ImportCard
            type="assignments" title="Item → Customer Assignments"
            description="Assign items to customers (which non-generic items a customer can see) and set per-item image-required."
            columns="sku, customer_slug, image_upload_required(yes/no)"
          />
        </div>
      </div>
    </>
  );
}
